import React from 'react';
import ServiceLayout from '../../../components/layout/ServiceLayout';
import Hero from '../../../components/sections/Hero';
import Card from '../../../components/ui/Card';
import FAQ from '../../../components/ui/FAQ';
import CallToAction from '../../../components/sections/CallToAction';
import { faqs, targetAudiences, services } from '../../../data/mockData';
import ServiceCard from '../../../components/ui/ServiceCard';
import FeatureCard from '../../../components/ui/FeatureCard';

const StartupsPage: React.FC = () => {
  // Get startup audience data
  const startupData = targetAudiences.find(audience => audience.id === 'startups');
  
  // Filter relevant services for startups
  const relevantServices = services.filter(service => 
    ['custom-ai-development', 'ai-training', 'workflow-automation'].includes(service.id)
  );

  // Filter relevant FAQs
  const relevantFaqs = faqs.filter((_, index) => [0, 2, 3].includes(index));
  
  return (
    <ServiceLayout>
      <Hero
        title="AI Solutions for Startups"
        subtitle="Leverage AI to accelerate growth, optimize limited resources, and create scalable products that stand out in competitive markets."
        ctaText="Accelerate Your Startup"
        ctaLink="/contact"
        backgroundType="gradient"
      />

      {/* Value Proposition Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl font-bold mb-6">Build Your Startup with AI from Day One</h2>
            <p className="text-lg text-gray-700">
              The most successful startups today are built with AI at their core. Whether you're creating an AI-native 
              product or using AI to optimize your operations, our startup-focused solutions help you move faster, 
              scale efficiently, and create capabilities that impress customers and investors alike.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="p-8">
              <h3 className="text-xl font-semibold mb-3">Accelerate Time to Market</h3>
              <p>
                Develop and launch products faster with AI-enhanced development, testing, and
                deployment processes that compress traditional timelines.
              </p>
            </Card>
            <Card className="p-8">
              <h3 className="text-xl font-semibold mb-3">Maximize Limited Resources</h3>
              <p>
                Do more with your small team and seed funding by leveraging AI to automate
                tasks and enhance capabilities across all business functions.
              </p>
            </Card>
            <Card className="p-8">
              <h3 className="text-xl font-semibold mb-3">Create Unfair Advantages</h3>
              <p>
                Build proprietary AI capabilities that create barriers to entry,
                differentiate your product, and create defensible market positions.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Startup Solutions */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">Tailored Solutions for Startup Success</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {startupData && (
              <div className="bg-white p-8 rounded-lg shadow-md">
                <h3 className="text-2xl font-semibold mb-4">{startupData.title}</h3>
                <p className="mb-6 text-gray-700">{startupData.description}</p>
                <ul className="space-y-3 mb-8">
                  {startupData.features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <span className="text-primary font-bold mr-2">•</span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="bg-primary bg-opacity-5 p-8 rounded-lg shadow-md">
              <h3 className="text-2xl font-semibold mb-4">We Help Startups With:</h3>
              <div className="space-y-4">
                <div>
                  <h4 className="text-xl font-medium mb-2">AI-Native Product Development</h4>
                  <p className="text-gray-700">
                    Build products with AI at their core, from initial concept through technical development and market launch.
                  </p>
                </div>
                <div>
                  <h4 className="text-xl font-medium mb-2">AI Enhancement of Existing Products</h4>
                  <p className="text-gray-700">
                    Add AI capabilities to your current product to increase value, create differentiation, and deliver better user experiences.
                  </p>
                </div>
                <div>
                  <h4 className="text-xl font-medium mb-2">Operational Efficiency</h4>
                  <p className="text-gray-700">
                    Implement AI-powered operations that allow your small team to accomplish more with less, extending your runway.
                  </p>
                </div>
                <div>
                  <h4 className="text-xl font-medium mb-2">Investor-Ready AI Strategy</h4>
                  <p className="text-gray-700">
                    Develop a compelling AI roadmap and technical strategy that strengthens your fundraising narrative.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AI Packages for Startups */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">Startup-Focused AI Solutions</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <FeatureCard
              title="AI Product Development"
              description="End-to-end support for creating AI-native products or adding AI capabilities to your existing product roadmap."
              icon="code"
              details={[
                "AI feasibility assessment",
                "Model selection and development",
                "Data strategy and implementation",
                "Integration and deployment",
                "Ongoing optimization"
              ]}
            />
            <FeatureCard
              title="Fractional AI Team"
              description="Access AI expertise without the cost of full-time hires. Our team acts as your AI department on a flexible, as-needed basis."
              icon="users"
              details={[
                "Technical co-founder support",
                "AI architecture and implementation",
                "Development team training",
                "Roadmap planning and execution",
                "Investor technical due diligence support"
              ]}
            />
            <FeatureCard
              title="Startup Growth Accelerator"
              description="AI-powered solutions specifically designed to help startups grow faster and more efficiently with limited resources."
              icon="trending-up"
              details={[
                "Marketing automation and optimization",
                "Customer acquisition AI systems",
                "User experience personalization",
                "Product-market fit analysis",
                "Scalable infrastructure design"
              ]}
            />
          </div>
        </div>
      </section>

      {/* Relevant Services Section */}
      {relevantServices.length > 0 && (
        <section className="py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">Our Services for Startups</h2>
            <div className="grid md:grid-cols-2 gap-8">
              {relevantServices.map(service => (
                <ServiceCard key={service.id} service={service} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Startup-Friendly Pricing */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">Startup-Friendly Engagement Models</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-md">
              <h3 className="text-2xl font-semibold mb-4">Equity + Cash Hybrid</h3>
              <p className="mb-6 text-gray-700">
                For early-stage startups, we offer flexible arrangements that combine reduced cash fees with equity participation, aligning our success with yours.
              </p>
              <p className="font-medium">Ideal for: Pre-seed and seed-stage startups with limited cash but strong growth potential.</p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-md">
              <h3 className="text-2xl font-semibold mb-4">Milestone-Based Pricing</h3>
              <p className="mb-6 text-gray-700">
                Our phased approach breaks projects into achievable milestones with separate pricing for each, helping manage cash flow and reduce upfront commitments.
              </p>
              <p className="font-medium">Ideal for: Startups with defined funding rounds seeking predictable expenses aligned with development stages.</p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-md">
              <h3 className="text-2xl font-semibold mb-4">Success-Based Fees</h3>
              <p className="mb-6 text-gray-700">
                For certain projects, we can structure arrangements where a portion of our compensation is based on achieving agreed-upon performance metrics or business outcomes.
              </p>
              <p className="font-medium">Ideal for: Growth-stage startups focused on specific KPIs like user acquisition, conversion improvements, or operational efficiency.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">Frequently Asked Questions</h2>
          <div className="max-w-3xl mx-auto">
            <FAQ items={relevantFaqs} />
          </div>
        </div>
      </section>

      {/* Key Stats */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">Why Startups Choose Us</h2>
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div className="p-8 bg-white rounded-lg shadow-md">
              <div className="text-5xl font-bold text-primary mb-2">76%</div>
              <p className="text-xl">Of our startup clients successfully raised their next funding round</p>
            </div>
            <div className="p-8 bg-white rounded-lg shadow-md">
              <div className="text-5xl font-bold text-primary mb-2">3.8x</div>
              <p className="text-xl">Average acceleration in product development timeline</p>
            </div>
            <div className="p-8 bg-white rounded-lg shadow-md">
              <div className="text-5xl font-bold text-primary mb-2">68%</div>
              <p className="text-xl">Average reduction in customer acquisition costs</p>
            </div>
            <div className="p-8 bg-white rounded-lg shadow-md">
              <div className="text-5xl font-bold text-primary mb-2">50+</div>
              <p className="text-xl">Successful startup AI implementations</p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <CallToAction
        title="Ready to accelerate your startup with AI?"
        description="Contact us today to discuss your startup's challenges and how our AI solutions can help you build faster, scale efficiently, and stand out in the market."
        ctaText="Supercharge Your Startup"
        ctaLink="/contact"
      />
    </ServiceLayout>
  );
};

export default StartupsPage;